// frontend/src/components/TimerWidget.js
import { useState, useEffect } from 'react';
import { Clock, Pause, Play, Square } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { useTimer } from '@/contexts/TimerContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function formatTime(totalSeconds) {
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

function authHeaders() {
  return { Authorization: `Bearer ${localStorage.getItem('lawliet_token')}` };
}

export default function TimerWidget() {
  const { timerState, pause, resume, stop, getElapsedMinutes } = useTimer();
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!timerState || timerState.paused_at) {
      setElapsedSeconds(Math.floor(getElapsedMinutes() * 60));
      return;
    }
    const interval = setInterval(() => {
      setElapsedSeconds(Math.floor((Date.now() - new Date(timerState.started_at)) / 1000));
    }, 1000);
    return () => clearInterval(interval);
  }, [timerState]); // eslint-disable-line

  if (!timerState) return null;

  const isPaused = !!timerState.paused_at;
  const elapsedMinutes = getElapsedMinutes();
  const roundedMinutes = Math.max(5, Math.ceil(elapsedMinutes / 5) * 5);
  const displayTitle =
    timerState.dossier_title.length > 28
      ? timerState.dossier_title.slice(0, 26) + '…'
      : timerState.dossier_title;

  const handleSave = async () => {
    if (!description.trim()) {
      toast.error('Veuillez saisir une description');
      return;
    }
    setSaving(true);
    try {
      await axios.post(
        `${API}/time-entries`,
        {
          dossier_id: timerState.dossier_id,
          description: description.trim(),
          date: new Date().toISOString().slice(0, 10),
          duration_minutes: roundedMinutes,
        },
        { headers: authHeaders() }
      );
      toast.success(`Saisie enregistrée — ${roundedMinutes} min`);
      stop();
      setShowModal(false);
      setDescription('');
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const handleAbandon = () => {
    if (description.trim() && !window.confirm('Abandonner cette saisie de temps ?')) return;
    setShowModal(false);
    stop();
    setDescription('');
  };

  return (
    <>
      {/* Floating timer widget */}
      <div
        className="fixed bottom-6 right-6 z-50 flex items-center gap-3 rounded-xl bg-slate-900 px-4 py-3 shadow-2xl"
        style={{ minWidth: '16rem' }}
      >
        <Clock size={16} className="flex-shrink-0 text-blue-400" />
        <div className="min-w-0 flex-1">
          <p className="truncate text-xs text-slate-400">{displayTitle}</p>
          <p className="font-mono text-lg font-semibold text-white">
            {formatTime(isPaused ? Math.floor(getElapsedMinutes() * 60) : elapsedSeconds)}
          </p>
        </div>
        <Button
          size="icon"
          variant="ghost"
          className="h-8 w-8 text-slate-300 hover:bg-slate-700 hover:text-white"
          onClick={isPaused ? resume : pause}
          title={isPaused ? 'Reprendre' : 'Mettre en pause'}
        >
          {isPaused ? <Play size={14} /> : <Pause size={14} />}
        </Button>
        <Button
          size="icon"
          variant="ghost"
          className="h-8 w-8 text-red-400 hover:bg-red-950 hover:text-red-300"
          onClick={() => setShowModal(true)}
          title="Arrêter et enregistrer"
        >
          <Square size={14} />
        </Button>
      </div>

      {/* Stop confirmation modal */}
      {showModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50">
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-2xl">
            <h3 className="mb-1 text-lg font-semibold text-slate-900">
              Enregistrer la saisie de temps
            </h3>
            <p className="mb-4 text-sm text-slate-500">
              {timerState.dossier_title} · {roundedMinutes} min (arrondi à 5 min)
            </p>
            <div className="mb-4">
              <Label htmlFor="timer-description" className="mb-1 block text-sm font-medium text-slate-700">
                Description *
              </Label>
              <Input
                id="timer-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Ex : Rédaction des conclusions"
                onKeyDown={(e) => e.key === 'Enter' && handleSave()}
                autoFocus
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={handleAbandon}>
                Abandonner
              </Button>
              <Button onClick={handleSave} disabled={saving}>
                {saving ? 'Enregistrement…' : 'Enregistrer'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
